import { formatNumber } from 'shared/utils';
import { getApiErrorMessage } from './api-result';
import type { ChangePrimaryEmailResult, Notice, PrimaryChangeAttempt } from './types';

/**
 * Builds the notice for a primary email change that failed, offering a retry with the same
 * parameters.
 */
export const buildPrimaryChangeFailureNotice = ({
  error,
  attempt,
}: {
  error: unknown;
  attempt: PrimaryChangeAttempt;
}): Notice => ({
  severity: 'warning',
  message: `Changing the primary email to ${attempt.email} failed: ${getApiErrorMessage(error, 'unknown error')}. Some records may already be updated. Retry to finish the change.`,
  retry: attempt,
});

/**
 * Builds the notice for an applied primary email change from the server's response. It only says
 * every record was updated when no record still carries the old address, and reports a failed
 * push-notification re-sync.
 */
export const buildPrimaryChangeNotice = ({
  result,
  attempt,
}: {
  result: ChangePrimaryEmailResult;
  attempt: PrimaryChangeAttempt;
}): Notice => {
  const { oldEmail, email: newEmail, keepOldAsSecondary } = attempt;
  const remaining = Object.values(result.counts).reduce((sum, count) => sum + count, 0);
  const kept = keepOldAsSecondary ? ` ${oldEmail} stays as a secondary email.` : '';
  const push =
    result.oneSignalSynced === false
      ? ' Push notifications will re-register the next time the user opens the app.'
      : '';

  if (remaining > 0) {
    return {
      severity: 'warning',
      message: `Primary email changed to ${newEmail}, but ${formatNumber(remaining)} records still carry ${oldEmail}. Retry to update them.${kept}${push}`,
      retry: attempt,
    };
  }
  return {
    severity: 'success',
    message: `Primary email changed to ${newEmail}. All records were updated; 0 still carry ${oldEmail}.${kept}${push}`,
  };
};
